<template>
  <MMPopupFullScreen>
    <div class="product-gallery-popup">
      <div class="product-gallery-popup__wrapper" :key="photos.length">
        <div class="navigation">
          <div v-if="showNavigationButtons" class="slider-button prev" @click="goToPrevious">
            <IconChevronLeftLarge :size="24" />
          </div>
          <div v-if="showNavigationButtons" class="slider-button next" @click="goToNext">
            <IconChevronRightLarge :size="24" />
          </div>
          <div class="slider-button close" @click="onClose">
            <IconClose :size="24" />
          </div>
        </div>

        <!-- Keen Slider Implementation -->
        <div class="keen-slider" ref="keenSlider">
          <div
            v-for="(photo, index) in photos"
            :key="index"
            class="slide"
            :class="{ active: index === currentIndex }"
            :style="{ transform: `translateX(${(index - currentIndex) * 100}%)` }"
          >
            <div class="image-container">
              <!-- TODO: Replace with ImgWithFallback from @/shared/ui when available -->
              <img
                class="image"
                :src="photo.original || photo.high || photo.thumbnail"
                :alt="alt"
                @load="onImageLoad"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </MMPopupFullScreen>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, nextTick, watch } from 'vue';
import { MMPopupFullScreen } from '@mm-frontend/mm-web-ui-kit';
import type { IPhoto } from '../../models/slider';
import { OverflowHiddenController } from '@/shared/helpers/OverflowHiddenController';
import { IconChevronLeftLarge, IconChevronRightLarge, IconClose } from '@/features/ui-kit/icons';

interface IPopupData {
  photos: IPhoto[];
  index?: number;
  alt?: string;
}

const props = defineProps<{ onShowData: IPopupData }>();

const emit = defineEmits<{
  close: [{ activeIndex: number }];
}>();

const photos = computed(() => props.onShowData?.photos || []);
const alt = computed(() => props.onShowData?.alt || '');
const initialIndex = computed(() => props.onShowData?.index || 0);

// Reactive state
const currentIndex = ref(initialIndex.value);
const keenSlider = ref<HTMLElement>();

// Watch for index prop changes
watch(
  () => initialIndex.value,
  (newIndex) => {
    if (
      newIndex !== undefined &&
      newIndex >= 0 &&
      photos.value.length > 0 &&
      newIndex < photos.value.length
    ) {
      currentIndex.value = newIndex;
    } else if (newIndex !== undefined && newIndex >= 0 && photos.value.length === 0) {
      currentIndex.value = 0;
    }
  },
  { immediate: true }
);

// Computed properties
const showNavigationButtons = computed(() => photos.value.length > 1);

// Methods
const goToPrevious = () => {
  if (currentIndex.value > 0) {
    currentIndex.value--;
  } else if (photos.value.length > 1) {
    currentIndex.value = photos.value.length - 1;
  }
};

const goToNext = () => {
  if (currentIndex.value < photos.value.length - 1) {
    currentIndex.value++;
  } else if (photos.value.length > 1) {
    currentIndex.value = 0;
  }
};

const onClose = () => {
  emit('close', { activeIndex: currentIndex.value });
};

const onImageLoad = () => {
  // Image loaded successfully
};

const keyHandler = (e: KeyboardEvent) => {
  switch (e.keyCode) {
    case 27: // Escape
      onClose();
      break;
    case 37: // Left arrow
      goToPrevious();
      break;
    case 39: // Right arrow
      goToNext();
      break;
  }
};

// Lifecycle
onMounted(async () => {
  await nextTick();
  window.addEventListener('keyup', keyHandler);
  OverflowHiddenController.enable();
});

onUnmounted(() => {
  window.removeEventListener('keyup', keyHandler);
  OverflowHiddenController.disable();
});
</script>

<style lang="stylus" scoped>
.product-gallery-popup
  overscroll-behavior none
  position fixed
  top 0
  left 0
  right 0
  bottom 0
  background rgba(0, 0, 0, 0.6)

  &__wrapper
    .navigation
      width 100%
      position absolute
      z-index 2
      height 100vh


      .slider-button
        position absolute
        top calc(50% - 20px)
        z-index 2
        border-radius 20px
        border 1px solid rgba(128, 128, 128, 0.3)
        background #fff
        transition all .3s ease-in-out
        opacity .5
        width 40px
        height 40px
        display flex
        align-items center
        justify-content center
        backdrop-filter blur(20px)
        -webkit-backdrop-filter blur(20px)
        cursor pointer

        &.next
          right 16px

        &.prev
          left 16px

        &.close
          right 16px
          top 16px

        &:hover
          opacity .7

        &:active
          opacity .9

    margin 0 auto
    border-radius 8px
    transform translateZ(0)

    .keen-slider
      position relative
      width 100%
      height 100vh
      overflow hidden

      .slide
        position absolute
        top 0
        left 0
        width 100%
        height 100%
        transition transform 0.3s ease-in-out

        &.active
          z-index 1

        .image-container
          display flex
          align-items center
          justify-content center
          width 100%
          height 100%

          .image
            max-width 100%
            max-height 100%
            object-fit contain
            user-select none
            pointer-events none
            background #EEE

  +Media(Mobile, Tablet)
    .keen-slider
      .slide
        .image-container
          .image
            width 100%
            height auto
</style>
